import React, { useEffect, useState } from "react";
import {
  Box,
  Divider,
  Grid,
  Paper,
  Typography,
  makeStyles,
} from "@material-ui/core";
import CalendarMonthIcon from "@material-ui/icons/DateRange";
import EmailIcon from "@material-ui/icons/Email";

import { GetAuctionByID } from "../../../adminModel/auctions";
import { findlot } from "../../../adminModel/lots";

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(3),
    backgroundColor: "#fff",
    borderRadius: 8,
  },

  section: {
    height: "100%",
  },

  sectionHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(1.5),
  },

  sectionIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.text.secondary,
  },

  sectionTitle: {
    fontWeight: 600,
    color: "#222",
  },

  divider: {
    marginBottom: theme.spacing(2.5),
  },

  infoRow: {
    marginBottom: theme.spacing(2.5),

    "&:last-child": {
      marginBottom: 0,
    },
  },

  label: {
    display: "block",
    marginBottom: theme.spacing(0.75),
    color: "#777",
    fontSize: 13,
    fontWeight: 500,
  },

  value: {
    color: "#222",
    fontSize: 14,
  },

  dateValue: {
    display: "inline-block",
    padding: theme.spacing(1, 1.5),
    backgroundColor: "#f7f7f7",
    border: "1px solid #e0e0e0",
    borderRadius: 6,
    color: "#333",
    fontSize: 14,
  },

  auctionLink: {
    color: theme.palette.primary.main,
    textDecoration: "none",
    fontWeight: 600,

    "&:hover": {
      textDecoration: "underline",
    },
  },

  bidder: {
    fontSize: 15,
    fontWeight: 600,
    color: "#222",
    marginBottom: theme.spacing(2.5),
  },

  bidAmount: {
    fontSize: 22,
    fontWeight: 700,
    color: "#222",
  },

  status: {
    display: "inline-block",
    padding: theme.spacing(0.5, 1),
    backgroundColor: "#f5f5f5",
    borderRadius: 4,
    fontSize: 13,
    fontWeight: 600,
    color: "#444",
  },
}));

const formatDate = (dateValue, hour12 = false) => {
  if (!dateValue) {
    return "N/A";
  }

  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) {
    return "N/A";
  }

  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12,
  });
};

const formatAmount = (amount) => {
  if (typeof amount === "number") {
    return amount.toLocaleString();
  }

  if (amount !== null && amount !== undefined && amount !== "") {
    return amount;
  }

  return "N/A";
};

export default function BaseField({ auctionID, something }) {
  const classes = useStyles();

  const [auctionData, setAuctionData] = useState({});
  const [lotData, setLotData] = useState({});

  useEffect(() => {
    if (!auctionID) {
      return;
    }

    const fetchAuction = async () => {
      try {
        const results = await GetAuctionByID(auctionID);

        if (results) {
          setAuctionData(results);
        } else {
          setAuctionData({});
        }
      } catch (error) {
        console.error("Failed to fetch auction details:", error);
        setAuctionData({});
      }
    };

    fetchAuction();
  }, [auctionID]);

  useEffect(() => {
    if (!auctionID || !something) {
      return;
    }

    const fetchLot = async () => {
      try {
        const results = await findlot(something,auctionID );

        if (results && results.data && results.data.length > 0) {
          setLotData(results.data[0]);
        } else {
          setLotData({});
        }
      } catch (error) {
        console.error("Failed to fetch lot details:", error);
        setLotData({});
      }
    };

    fetchLot();
  }, [auctionID, something]);

  return (
    <Paper elevation={1} className={classes.paper}>
      <Grid container spacing={4}>
        {/* =========================
            LOT / AUCTION DETAILS
        ========================== */}
        <Grid item xs={12} md={6}>
          <Box className={classes.section}>
            <Box className={classes.sectionHeader}>
              <CalendarMonthIcon className={classes.sectionIcon} />

              <Typography
                variant="h6"
                className={classes.sectionTitle}
              >
                Lot Dates
              </Typography>
            </Box>

            <Divider className={classes.divider} />

            {/* Date Created */}
            <Box className={classes.infoRow}>
              <Typography className={classes.label}>
                Date Created
              </Typography>

              <Typography className={classes.value}>
                {formatDate(auctionData.createdDate)}
              </Typography>
            </Box>

            {/* Start Date */}
            <Box className={classes.infoRow}>
              <Typography className={classes.label}>
                Start Time
              </Typography>

              <Box className={classes.dateValue}>
                {formatDate(auctionData.date_start, true)}
              </Box>
            </Box>

            {/* End Date */}
            <Box className={classes.infoRow}>
              <Typography className={classes.label}>
                Closing Date
              </Typography>

              <Box className={classes.dateValue}>
                {formatDate(auctionData.date_end, true)}
              </Box>
            </Box>

            {/* Auction */}
            <Box className={classes.infoRow}>
              <Typography className={classes.label}>
                Auction
              </Typography>

              {auctionData.auction_name ? (
                <a
                  href={`/app/auctions/view/details/${auctionData.auction_name}/${auctionID}`}
                  className={classes.auctionLink}
                >
                  {auctionData.auction_name}
                </a>
              ) : (
                <Typography className={classes.value}>
                  N/A
                </Typography>
              )}
            </Box>
          </Box>
        </Grid>

        {/* =========================
            HIGHEST BIDDER
        ========================== */}
        <Grid item xs={12} md={6}>
          <Box className={classes.section}>
            <Box className={classes.sectionHeader}>
              <EmailIcon className={classes.sectionIcon} />

              <Typography
                variant="h6"
                className={classes.sectionTitle}
              >
                Highest Bidder
              </Typography>
            </Box>

            <Divider className={classes.divider} />

            {/* Bidder */}
            <Box className={classes.infoRow}>
              <Typography className={classes.label}>
                Bidder
              </Typography>

              <Typography className={classes.bidder}>
                {lotData.id || "N/A"}
                {lotData.email ? ` - ${lotData.email}` : ""}
              </Typography>
            </Box>

            {/* Email */}
            <Box className={classes.infoRow}>
              <Typography className={classes.label}>
                Email
              </Typography>

              <Typography className={classes.value}>
                {lotData.email || "N/A"}
              </Typography>
            </Box>

            {/* Highest Bid */}
            <Box className={classes.infoRow}>
              <Typography className={classes.label}>
                Highest Bid
              </Typography>

              <Typography className={classes.bidAmount}>
                Php {formatAmount(lotData.hp)}
              </Typography>
            </Box>

            {/* Status */}
            <Box className={classes.infoRow}>
              <Typography className={classes.label}>
                Status
              </Typography>

              <Typography className={classes.status}>
                {lotData.lot_status || "N/A"}
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
}